import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore
} from 'react';
import type { DraftStore } from './createDraftStore';
import type { DraftKeySpec } from './draftKey';
import type { DraftValues } from './draftTypes';

/**
 * What a row reads its controls through.
 *
 * The two predicates are deliberately NOT the same, and neither is called
 * `isDirty` — that name is already taken in the opener row, with whole-row
 * scope.
 */
export interface DraftAccessor<V extends DraftValues> {
  /** What the control shows: the operator's value, else the confirmed one. */
  value: <K extends keyof V & string>(field: K) => V[K];
  /** What a write would be compared against. */
  confirmed: <K extends keyof V & string>(field: K) => V[K];
  /** The operator interacted. Decides the CREATE payload. */
  touched: (field: keyof V & string) => boolean;
  /** Differs from confirmed. Decides the ✓, the tint, and the PATCH payload. */
  diverges: (field: keyof V & string) => boolean;
  /** Any field diverges — the whole-row ✓. */
  anyDiverges: () => boolean;
  /** Every field that diverges, in catalog order. */
  divergedFields: () => (keyof V & string)[];
  /** Every field the operator touched, in catalog order. */
  touchedFields: () => (keyof V & string)[];

  set: <K extends keyof V & string>(field: K, value: V[K]) => void;
  confirmWith: (values: Partial<V>) => void;
  fail: (fields: readonly (keyof V & string)[]) => void;
  discard: () => void;
}

/**
 * Bind one row to its draft.
 *
 * `baseline` is what the SERVER says each control should read — the same
 * expressions the row already computes off its rule. Pass it memoised; the
 * accessor recomputes with it.
 *
 * This replaces the row's re-sync effects outright rather than guarding them.
 * An untouched field reads its baseline straight through on every render, so
 * there is no window in which a poll can overwrite anything; a touched one
 * ignores the baseline by construction, so a server value that changes
 * underneath moves the ✓ instead of the input.
 */
export function useDraftRow<V extends DraftValues, K>(
  store: DraftStore,
  keySpec: DraftKeySpec<K>,
  rowIdentity: K,
  baseline: V
): DraftAccessor<V> {
  const rowKey = useMemo(
    () => keySpec.encode(rowIdentity),
    [keySpec, rowIdentity]
  );

  // The inline arrow is load-bearing — do NOT "optimise" it into a
  // `useCallback([store, rowKey])`. `resetAll()` drops every entry, listener
  // set included, while rows are still mounted; re-subscribing on each commit
  // is what re-registers them. A memoised subscribe would leave orphans React
  // never re-registers and those rows would silently freeze.
  //
  // (`clear()` is not the hazard — it empties `cells` and leaves the entry.
  // `live/useLiveStore` carries the same warning for its own eviction path.)
  const row = useSyncExternalStore(
    cb => store.subscribeRow(rowKey, cb),
    () => store.getRowSnapshot(rowKey)
  );

  // Drop cells that stopped carrying information. In an effect, never in
  // render.
  //
  // `row` is in the deps, not just `baseline`: a cell can stop being
  // informative because the OPERATOR moved it (typed a value back to what the
  // server already had), not only because the server moved. Keyed on the
  // baseline alone, such a cell survives until the next server change — and by
  // then it compares against the NEW value, so it reads as a genuine edit and
  // shadows it. Reconciling on the draft too converges in one extra pass: a
  // no-op reconcile writes nothing and notifies nobody.
  useEffect(() => {
    store.reconcile(rowKey, baseline);
  }, [store, rowKey, baseline, row]);

  const fieldNames = useMemo(
    () => Object.keys(store.fields) as (keyof V & string)[],
    [store]
  );

  const confirmed = useCallback(
    <K extends keyof V & string>(field: K): V[K] => {
      const cell = row?.cells[field];
      return (cell && 'confirmed' in cell
        ? (cell.confirmed as V[K])
        : baseline[field]) as V[K];
    },
    [row, baseline]
  );

  const value = useCallback(
    <K extends keyof V & string>(field: K): V[K] => {
      const cell = row?.cells[field];
      return cell?.touched ? (cell.value as V[K]) : confirmed(field);
    },
    [row, confirmed]
  );

  const touched = useCallback(
    (field: keyof V & string) => row?.cells[field]?.touched === true,
    [row]
  );

  const diverges = useCallback(
    (field: keyof V & string) => !Object.is(value(field), confirmed(field)),
    [value, confirmed]
  );

  const divergedFields = useCallback(
    () => fieldNames.filter(diverges),
    [fieldNames, diverges]
  );

  const touchedFields = useCallback(
    () => fieldNames.filter(touched),
    [fieldNames, touched]
  );

  const anyDiverges = useCallback(
    () => fieldNames.some(diverges),
    [fieldNames, diverges]
  );

  const set = useCallback(
    <K extends keyof V & string>(field: K, next: V[K]) => {
      store.set(rowKey, field, next);
    },
    [store, rowKey]
  );

  // The anchor has to be the baseline AT THE ACK, not at the click. A write is
  // a round-trip, and the optimistic merge already moves the cache during it —
  // reading it out of the handler's closure would anchor to a value that is
  // one render stale before the response even lands, and a third party moving
  // the rule mid-flight (a bulk, another tab) would then retire the override
  // the instant it was installed, flashing the row backwards.
  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;

  const confirmWith = useCallback(
    (values: Partial<V>) => {
      store.confirm(rowKey, values as DraftValues, baselineRef.current as DraftValues);
    },
    [store, rowKey]
  );

  const fail = useCallback(
    (failed: readonly (keyof V & string)[]) => {
      store.fail(rowKey, failed as readonly string[]);
    },
    [store, rowKey]
  );

  const discard = useCallback(() => {
    store.clear(rowKey);
  }, [store, rowKey]);

  return {
    value,
    confirmed,
    touched,
    diverges,
    anyDiverges,
    divergedFields,
    touchedFields,
    set,
    confirmWith,
    fail,
    discard
  };
}
